import { useSearchParams } from 'react-router-dom';
import { useTasks, useProjects } from '@/hooks/useTasks.ts';
import { TaskRow } from './TaskRow.tsx';
import { TaskDrawer } from './TaskDrawer.tsx';
import { QuickCapture } from './QuickCapture.tsx';
import { PageHeader, EmptyState, SectionLabel } from './ui.tsx';
import type { DuePreset } from '@/lib/dates.ts';
import type { TaskFilter, TaskSort, TaskStatus } from '@shared/types';

interface Props {
  title: string;
  subtitle?: string;
  filter: TaskFilter;
  sorts?: TaskSort[];
  /** What this screen is for, and what to do next — never a status report. */
  empty: { title: string; body: string };
  captureStatus?: TaskStatus;
  captureProjectId?: string | null;
  captureDue?: DuePreset | null;
  capturePlaceholder?: string;
  showProject?: boolean;
}

export function TaskListView({
  title,
  subtitle,
  filter,
  sorts,
  empty,
  captureStatus = 'inbox',
  captureProjectId = null,
  captureDue = null,
  capturePlaceholder,
  showProject = true,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskId = searchParams.get('task');

  const { data: tasks, isPending, isError } = useTasks(filter, sorts);
  const { data: projects } = useProjects();
  const projectNames = new Map((projects ?? []).map((p) => [p.id, p.name]));

  function openTask(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('task', id);
      return next;
    });
  }

  function closeTask() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }

  const open = (tasks ?? []).filter((t) => t.status !== 'done' && t.status !== 'cancelled');
  const closed = (tasks ?? []).filter((t) => t.status === 'done' || t.status === 'cancelled');

  const row = (task: (typeof open)[number]) => (
    <TaskRow
      key={task.id}
      task={task}
      onOpen={openTask}
      showProject={showProject}
      projectName={task.project_id ? (projectNames.get(task.project_id) ?? null) : null}
    />
  );

  return (
    <div className="mx-auto w-full max-w-[1120px] px-5 py-8 sm:px-8">
      <PageHeader
        title={title}
        {...(subtitle ? { subtitle } : {})}
        {...(tasks ? { count: `${open.length} open` } : {})}
      />

      <QuickCapture
        defaultStatus={captureStatus}
        defaultProjectId={captureProjectId}
        defaultDue={captureDue}
        {...(capturePlaceholder ? { placeholder: capturePlaceholder } : {})}
      />

      {isPending && <p className="t-meta">Loading…</p>}

      {isError && (
        <p role="alert" className="t-meta text-brick">
          Cannot reach the server.
        </p>
      )}

      {tasks && open.length === 0 && closed.length === 0 && (
        <EmptyState title={empty.title} body={empty.body} />
      )}

      {open.length > 0 && <ul className="border-t border-line">{open.map(row)}</ul>}

      {closed.length > 0 && (
        <div className="mt-10">
          <SectionLabel count={closed.length}>Done</SectionLabel>
          <ul className="border-t border-line opacity-60 transition-opacity duration-[160ms] ease-out hover:opacity-100">
            {closed.map(row)}
          </ul>
        </div>
      )}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={closeTask} />}
    </div>
  );
}
